import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, LogOut, List } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useJobSearch } from '@/context/JobSearchContext';

export default function Navbar({ isInMobileMenu }) {
  const { isSignedIn, userDisplayName, userPhotoURL, handleGoogleSignIn, handleSignOut } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const { lastSearch } = useJobSearch();

  // Handle clicking outside of dropdown to close it
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  // Handle sign in click
  const onSignInClick = async () => {
    if (!isSignedIn) {
      await handleGoogleSignIn();
    } else {
      setDropdownOpen(!dropdownOpen);
    }
  };

  // Handle sign out click
  const onSignOutClick = async () => {
    await handleSignOut();
    setDropdownOpen(false);
  };

  // For debugging
  useEffect(() => {
    console.log('Auth State:', { isSignedIn, userDisplayName, userPhotoURL });
  }, [isSignedIn, userDisplayName, userPhotoURL]);

  // Get first name to display
  const getFirstName = () => {
    if (!userDisplayName) return '';
    return userDisplayName.split(' ')[0];
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        className={`flex items-center gap-2 rounded-full ${
          isInMobileMenu
            ? `bg-[#4054E6] hover:bg-[#3243b5] px-4 py-1.5 shadow-md w-full justify-center`
            : `bg-[#4054E6] hover:bg-[#3243b5] px-4 py-1.5 shadow-md`
        } text-white text-xs font-semibold transition-all`}
        onClick={onSignInClick}
      >
        {isSignedIn ? (
            <>
              <img
                  src={userPhotoURL || '/default_profile_picture.png'}
                  alt={userDisplayName || 'User'}
                  className={`${isInMobileMenu ? 'w-6 h-6' : 'w-6 h-6'} rounded-full object-cover border border-white/30`}
                  onError={(e) => {
                    console.error('Failed to load profile image:', e);
                    e.target.src = '/default_profile_picture.png';
                  }}
              />
              <span className="text-xs font-semibold">{userDisplayName}</span>
              <ChevronDown className="w-3 h-3" />
            </>
        ) : (
          <>
            <div className={`${isInMobileMenu ? 'w-6 h-6' : 'w-6 h-6'} flex-shrink-0 flex items-center justify-center`}>
              <svg viewBox="0 0 48 48" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                <path fill="none" d="M0 0h48v48H0z"></path>
              </svg>
            </div>
            <span className="text-xs font-semibold whitespace-nowrap">Sign in with Google</span>
          </>
        )}
      </button>

      {/* Dropdown menu */}
      {dropdownOpen && isSignedIn && (
        <div
          ref={dropdownRef}
          style={{ 
            minWidth: buttonRef.current ? buttonRef.current.offsetWidth : 'auto',
            transform: dropdownOpen ? 'translateY(0)' : 'translateY(-10px)',
            opacity: dropdownOpen ? 1 : 0
          }}
          className={`${isInMobileMenu ? 'w-full' : 'right-0'} absolute mt-2 bg-white rounded-lg shadow-xl py-2 z-50 border border-gray-100 transform origin-top transition-all duration-200 ease-out`}
        >

          {lastSearch?.exists === true ? (
              <button
                  onClick={(e) => {
                    e.preventDefault();
                    const element = document.getElementById('job-results');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                    setDropdownOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 flex items-center justify-center gap-2 transition-colors duration-150"
              >
                <List className="w-4 h-4 text-[#4054E6]" />
                <span className="font-medium">Results</span>
              </button>
          ) : null}

          <button
            onClick={onSignOutClick}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 flex items-center justify-center gap-2 transition-colors duration-150"
          >
            <LogOut className="w-4 h-4 text-[#4054E6]" />
            <span className="font-medium">Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
} 